import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  NO_RUN_UNTIL_LIMIT,
  isReached,
  useLimitState,
} from '@/features/spend-limits';
import type { TaskView } from '@/lib/api/generated/model';
import type { BoardStepSection } from '../boardModel';
import { reverseTransitionExists } from '../boardPower';
import { MOVE_FAILED_CARD_RETURNED } from '../errors';
import { useOpenLinkedAccounts } from '@/features/identity';
import { refusalToastOptions } from '../moveRefusalToast';
import { useMoveTask, type MoveTaskInput } from './useMoveTask';

/** How long the way back stays open (spec 098 FR-002). */
export const UNDO_WINDOW_MS = 8000;

export interface MoveContext {
  /** The step the task is leaving. */
  fromStepId: string;
  /** Every step on the board, for the label and the declared way back. */
  steps: readonly BoardStepSection[];
}

interface LastMove {
  taskId: string;
  from: string;
  to: string;
  version: number;
  expires: number;
  toastId: string;
  canUndo: boolean;
}

/**
 * A move that can be taken back (spec 098 FR-001 – FR-004).
 *
 * Wraps the ordinary move: on success it names the destination and, when the
 * workflow declares a transition from there back to where the card came from,
 * offers Undo for a short window. Undo is one more move — back, with the
 * task's version as the first move returned it, and the reason "Undo" — and a
 * refusal is stated exactly as any refusal is. Only the latest move can be
 * undone: a newer one withdraws the older offer, and so does a live refresh
 * that shows the card has moved on since.
 *
 * The record lives in a ref so `undo` and the toast's button always see the
 * latest move; a counter in state re-renders whoever reads `canUndo`.
 */
export function useUndoableMove(workspaceId: string, workflowId?: string) {
  const moveTask = useMoveTask();
  const openLinkedAccounts = useOpenLinkedAccounts();
  /*
    Whether a run will actually start (spec 101 US2).

    The move itself is untouched: the service allows it, and this feature does
    not prevent, delay or alter it (FR-010). What changes is that the
    confirmation stops being silent about the run it did not start — the exact
    case the reporter hit, where a card slid into the next step, said "Moved
    to…", and nothing ever ran.
  */
  const limits = useLimitState(workspaceId, workflowId);
  const limitReached =
    isReached(limits.workspace) || isReached(limits.workflow);
  // Two homes for one record: the ref is what the toast's button and `undo`
  // read (always current, never a stale closure); the state is what a render
  // reads for `canUndo`. `remember` keeps them equal. Expiry is not computed
  // in render: the toast's own duration is the window, and its close clears
  // the record; `undoMove` re-checks the clock before sending anything.
  const last = useRef<LastMove | null>(null);
  const [record, setRecord] = useState<LastMove | null>(null);
  const remember = useCallback((next: LastMove | null) => {
    if (last.current && last.current !== next) {
      toast.dismiss(last.current.toastId);
    }
    last.current = next;
    setRecord(next);
  }, []);

  const undoMove = useCallback(
    (record: LastMove) => {
      if (Date.now() > record.expires) return;
      remember(null);
      moveTask.mutate(
        {
          workspaceId,
          taskId: record.taskId,
          targetStepId: record.from,
          expectedVersion: record.version,
          reason: 'Undo',
        },
        {
          onSuccess: (result) => {
            if (result.refusal) {
              toast.error(
                result.refusal.message,
                refusalToastOptions(result.refusal, openLinkedAccounts),
              );
            }
          },
          onError: () => {
            toast.error(MOVE_FAILED_CARD_RETURNED);
          },
        },
      );
    },
    [moveTask, openLinkedAccounts, remember, workspaceId],
  );

  const move = useCallback(
    (input: MoveTaskInput, context: MoveContext) => {
      moveTask.mutate(input, {
        onSuccess: (result) => {
          if (result.refusal) {
            toast.error(
              result.refusal.message,
              refusalToastOptions(result.refusal, openLinkedAccounts),
            );
            return;
          }
          const destination = context.steps.find(
            (step) => step.stepId === input.targetStepId,
          );
          const canUndo = reverseTransitionExists(
            context.steps,
            context.fromStepId,
            input.targetStepId,
          );
          const record: LastMove = {
            taskId: input.taskId,
            from: context.fromStepId,
            to: input.targetStepId,
            version: result.task.version,
            expires: Date.now() + UNDO_WINDOW_MS,
            toastId: `move:${input.taskId}:${String(Date.now())}`,
            canUndo,
          };
          remember(record);
          toast(`Moved to ${destination?.label ?? input.targetStepId}`, {
            id: record.toastId,
            duration: UNDO_WINDOW_MS,
            // Beneath the confirmation, not instead of it: the move did happen,
            // and both facts are true at once.
            description: limitReached ? NO_RUN_UNTIL_LIMIT : undefined,
            // Offered only when the way back is declared (FR-002). An Undo the
            // service would refuse is a promise the board cannot keep.
            action: canUndo
              ? { label: 'Undo', onClick: () => undoMove(record) }
              : undefined,
            onAutoClose: () => {
              if (last.current === record) remember(null);
            },
          });
        },
        onError: () => {
          toast.error(MOVE_FAILED_CARD_RETURNED);
        },
      });
    },
    [limitReached, moveTask, openLinkedAccounts, remember, undoMove],
  );

  const undo = useCallback(() => {
    const record = last.current;
    if (record?.canUndo) undoMove(record);
  }, [undoMove]);

  /** Withdraw the offer when the board shows the card somewhere else (FR-004). */
  const withdrawIfMoved = useCallback(
    (tasks: readonly TaskView[]) => {
      const record = last.current;
      if (!record) return;
      const task = tasks.find((t) => t.id === record.taskId);
      if (task && task.position !== record.to) remember(null);
    },
    [remember],
  );

  return {
    move,
    undo,
    canUndo: Boolean(record?.canUndo),
    withdrawIfMoved,
  };
}
